import React, { useEffect, useMemo } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import {
  createGame,
  applyAction,
  getPlayerView,
  DEFAULT_CONFIG,
  type HHState,
  type HHConfig,
  type HHAction,
} from '../../../games/henhur/henhur';
import type { TokenType } from './types/game.types';
import HenHurGameEnhanced from './HenHurGameEnhanced';
import type { RoomView } from '../../../types/room';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: HHState | null;
  onFinish: () => void;
}

export default function HenHurRoomGame({ slug, room, myId, isLeader, initialState, onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<HHState>(slug);

  useEffect(() => {
    if (!isLeader || !initialState) return;
    startRelay('henhur', initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playerView = useMemo(() => {
    if (!state) return null;
    return getPlayerView(state, myId);
  }, [state, myId]);

  if (!playerView) {
    return (
      <div className="flex items-center justify-center w-full h-screen bg-gray-900">
        <div className="text-white text-xl">Loading HenHur...</div>
      </div>
    );
  }

  const dispatch = (action: HHAction) => {
    if (!state) return;
    const newState = applyAction(state, action);
    sendAction(action, newState);
  };

  const handleSelectRaceCard = (cardId: string, willBurn: boolean, tokensToUse: TokenType[]) => {
    dispatch({ type: 'select_race_card', playerId: myId, cardId, willBurn, tokensToUse });
  };

  const handleSelectAuctionCard = (cardId: string, willBurn: boolean, tokensToUse: TokenType[]) => {
    dispatch({ type: 'select_auction_card', playerId: myId, cardId, willBurn, tokensToUse });
  };

  const handleDraftCard = (cardId: string) => {
    dispatch({ type: 'draft_card', playerId: myId, cardId });
  };

  return (
    <HenHurGameEnhanced
      gameState={playerView}
      onSelectRaceCard={handleSelectRaceCard}
      onSelectAuctionCard={handleSelectAuctionCard}
      onDraftCard={handleDraftCard}
      isLeader={isLeader}
    />
  );
}

export { createGame as createHenHurGame, DEFAULT_CONFIG as HENHUR_DEFAULT_CONFIG };
export type { HHState as HenHurState, HHConfig as HenHurConfig };
